import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertOctagon,
  Camera,
  Database,
  Download,
  Loader2,
  ShieldAlert,
  Skull,
  Zap,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DangerousConfirmDialog } from '@/components/ui/dangerous-confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { useRole } from '@/hooks/useRole';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { formatDateTimeDZ, formatNumberDZ } from '@/lib/format';

type Scope =
  | 'finances'
  | 'crm'
  | 'echeances'
  | 'marketing'
  | 'formation'
  | 'rh'
  | 'complete'
  | 'total';

interface ScopeMeta {
  key: Scope;
  title: string;
  challenge: string;
  consequences: string[];
  available: boolean;
  level: 'module' | 'complete' | 'total';
  icon: React.ComponentType<{ className?: string }>;
}

const SCOPES: ScopeMeta[] = [
  {
    key: 'finances',
    title: 'Réinitialiser Finances',
    challenge: 'RESET_FINANCES',
    consequences: [
      'Supprime toutes les opérations',
      'Supprime tous les justificatifs (Storage + DB)',
      'Supprime toutes les clôtures',
      'Supprime tous les rapprochements',
      'Remet les soldes initiaux des comptes/caisses à 0',
      'GARDE : comptes, caisses, tiers, services',
    ],
    available: true,
    level: 'module',
    icon: Database,
  },
  {
    key: 'crm',
    title: 'Réinitialiser CRM',
    challenge: 'RESET_CRM',
    consequences: [
      'Supprime toutes les opportunités + activités + historique',
      'Supprime tous les contacts liés aux opportunités',
      'GARDE : pipelines, étapes, motifs de perte, quotas commerciaux',
    ],
    available: true,
    level: 'module',
    icon: Database,
  },
  {
    key: 'echeances',
    title: 'Réinitialiser Échéances',
    challenge: 'RESET_ECHEANCES',
    consequences: [
      'Supprime toutes les échéances (créances clients + dettes fournisseurs)',
      'Supprime toutes les relances et plans de paiement',
      'GARDE : templates de relance, tiers, banques',
    ],
    available: true,
    level: 'module',
    icon: Database,
  },
  {
    key: 'formation',
    title: 'Réinitialiser Formation',
    challenge: 'RESET_FORMATION',
    consequences: [
      'Supprime catalogue formations + sessions + inscriptions',
      'Supprime toutes les présences et évaluations',
      'GARDE : formateurs, salles',
    ],
    available: true,
    level: 'module',
    icon: Database,
  },
  {
    key: 'marketing',
    title: 'Réinitialiser Marketing',
    challenge: 'RESET_MARKETING',
    consequences: ['Supprime campagnes/leads (Sprint 6 — non livré)'],
    available: false,
    level: 'module',
    icon: Database,
  },
  {
    key: 'rh',
    title: 'Réinitialiser RH',
    challenge: 'RESET_RH',
    consequences: ['Supprime salariés/paies/congés (Sprint 6 — non livré)'],
    available: false,
    level: 'module',
    icon: Database,
  },
];

const COMPLETE: ScopeMeta = {
  key: 'complete',
  title: 'RESET COMPLET — Vider toutes les données opérationnelles',
  challenge: 'RESET_COMPLET',
  consequences: [
    'Supprime TOUTES les données métier (Finances + Échéances + CRM + Formation)',
    'Remet les soldes initiaux à 0',
    'GARDE : entreprises, activités, services, plan comptable, banques, caisses, tiers, formateurs, pipelines, utilisateurs',
  ],
  available: true,
  level: 'complete',
  icon: AlertOctagon,
};

const TOTAL: ScopeMeta = {
  key: 'total',
  title: 'RESET TOTAL — Tout supprimer sauf les utilisateurs',
  challenge: 'RESET_TOTAL_CONFIRME',
  consequences: [
    'Supprime opérations, justificatifs, clôtures, rapprochements',
    'Supprime aussi tiers, services et audit_log',
    'GARDE uniquement : utilisateurs (auth), structure entreprise/activités, banques système, plan SCF officiel',
  ],
  available: true,
  level: 'total',
  icon: Skull,
};

interface BackupItem {
  name: string;
  created_at: string;
  size: number;
}

export function AdminPage(): JSX.Element {
  const { role, isAdmin, loading } = useRole();
  const [active, setActive] = useState<ScopeMeta | null>(null);
  const [running, setRunning] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [backupsLoading, setBackupsLoading] = useState(false);

  const refreshBackups = async (): Promise<void> => {
    setBackupsLoading(true);
    try {
      const { data, error } = await supabase.storage.from('backups').list('', {
        sortBy: { column: 'created_at', order: 'desc' },
        limit: 50,
      });
      if (error || !data) return;
      const all: BackupItem[] = [];
      for (const f of data) {
        if (f.id) {
          all.push({ name: f.name, created_at: f.created_at ?? '', size: f.metadata?.size ?? 0 });
        } else {
          const sub = await supabase.storage.from('backups').list(f.name, {
            sortBy: { column: 'created_at', order: 'desc' },
            limit: 20,
          });
          if (sub.data) {
            for (const s of sub.data) {
              all.push({
                name: `${f.name}/${s.name}`,
                created_at: s.created_at ?? '',
                size: s.metadata?.size ?? 0,
              });
            }
          }
        }
      }
      setBackups(all);
    } finally {
      setBackupsLoading(false);
    }
  };

  useEffect(() => {
    if (role === 'super_admin') void refreshBackups();
  }, [role]);

  // Only redirect once role is actually known — never gate on `loading`,
  // which can stall and trap the page on a spinner forever.
  if (!loading && role !== null && role !== 'super_admin') {
    return <Navigate to="/parametres" replace />;
  }

  const runReset = async (scope: Scope, reason: string): Promise<void> => {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-reset', {
        body: { scope, reason },
      });
      if (error || (data && (data as { error?: string }).error)) {
        toast.error('Reset KO : ' + (error?.message ?? (data as { error?: string }).error));
        return;
      }
      const r = data as {
        tables_wiped: string[];
        counts: Record<string, number>;
        backup_path: string;
      };
      const total = Object.values(r.counts).reduce((a, b) => a + b, 0);
      toast.success(
        `Reset OK · ${formatNumberDZ(total)} ligne(s) wipées · backup ${r.backup_path}`,
      );
      void refreshBackups();
    } catch (e) {
      toast.error('Erreur : ' + (e as Error).message);
    } finally {
      setRunning(false);
      setActive(null);
    }
  };

  const runSnapshot = async (): Promise<void> => {
    setSnapshotting(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-snapshot', { body: {} });
      if (error || (data && (data as { error?: string }).error)) {
        toast.error('Snapshot KO : ' + (error?.message ?? (data as { error?: string }).error));
        return;
      }
      const r = data as { path: string; counts: Record<string, number> };
      const total = Object.values(r.counts).reduce((a, b) => a + b, 0);
      toast.success(`Snapshot créé · ${formatNumberDZ(total)} lignes · ${r.path}`);
      void refreshBackups();
    } finally {
      setSnapshotting(false);
    }
  };

  const downloadBackup = async (path: string): Promise<void> => {
    const { data, error } = await supabase.storage.from('backups').createSignedUrl(path, 600);
    if (error || !data) {
      toast.error('Lien indisponible : ' + (error?.message ?? '?'));
      return;
    }
    window.open(data.signedUrl, '_blank');
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Zone Admin"
        subtitle="Réinitialisation des données opérationnelles. Réservé super_admin."
        actions={<Badge variant="destructive">Zone dangereuse</Badge>}
      />

      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
        <div className="flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 shrink-0 text-destructive" />
          <div className="space-y-1 text-sm">
            <p className="font-semibold text-destructive">Précautions obligatoires :</p>
            <ul className="list-disc space-y-0.5 pl-5 text-destructive/80">
              <li>
                Chaque action exporte un backup JSON automatique dans le bucket privé{' '}
                <code className="font-mono">backups</code>
              </li>
              <li>Une saisie textuelle exacte est requise pour confirmer chaque opération</li>
              <li>
                Un motif (≥ 10 caractères) est journalisé dans <code>audit_log</code>
              </li>
              <li>Aucune action n&apos;est réversible : utilise un Snapshot avant si tu hésites</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Snapshot */}
      <section className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold">
              <Camera className="h-4 w-4 text-primary" /> Snapshot complet
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Capture toute la base (19 tables) en JSON dans le bucket{' '}
              <code>backups/snapshots/</code>. Utile avant un changement risqué.
            </p>
          </div>
          <Button onClick={runSnapshot} disabled={snapshotting}>
            {snapshotting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Camera className="h-4 w-4" />
            )}
            Créer un snapshot
          </Button>
        </div>
      </section>

      {/* Reset par module */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          A. Reset par module (granulaire)
        </h2>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {SCOPES.map((s) => (
            <ScopeCard key={s.key} scope={s} onClick={() => setActive(s)} />
          ))}
        </div>
      </section>

      {/* Reset complet */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          B. Reset complet
        </h2>
        <ScopeCard scope={COMPLETE} onClick={() => setActive(COMPLETE)} large />
      </section>

      {/* Reset total */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          C. Reset total (nuke nucléaire)
        </h2>
        <ScopeCard scope={TOTAL} onClick={() => setActive(TOTAL)} large />
      </section>

      {/* Backups */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Backups & snapshots disponibles ({backups.length})
        </h2>
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Fichier</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-right">Taille (kB)</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {backupsLoading ? (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-xs text-muted-foreground">
                    <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Chargement…
                  </td>
                </tr>
              ) : backups.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-xs text-muted-foreground">
                    Aucun backup pour le moment.
                  </td>
                </tr>
              ) : (
                backups.map((b) => (
                  <tr
                    key={b.name}
                    className="border-b border-border last:border-0 hover:bg-muted/20"
                  >
                    <td className="px-3 py-2 font-mono text-xs">{b.name}</td>
                    <td className="px-3 py-2 text-xs">
                      {b.created_at ? formatDateTimeDZ(b.created_at) : '—'}
                    </td>
                    <td className="num px-3 py-2 text-right text-xs">
                      {formatNumberDZ(Math.round(b.size / 1024))}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => void downloadBackup(b.name)}>
                        <Download className="h-3.5 w-3.5" /> Télécharger
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {active && (
        <DangerousConfirmDialog
          open={!!active}
          onOpenChange={(o) => !o && setActive(null)}
          title={active.title}
          description={
            active.available
              ? `Vous êtes sur le point d'exécuter une action de niveau "${active.level}". Une copie de sauvegarde JSON sera créée avant suppression.`
              : `Module pas encore livré. Action désactivée.`
          }
          consequences={active.consequences}
          challenge={active.challenge}
          requireReason
          confirmLabel={active.available ? 'Exécuter le reset' : 'Indisponible'}
          loading={running}
          onConfirm={async (reason) => {
            if (!active.available) return;
            await runReset(active.key, reason ?? '');
          }}
        />
      )}

      <p className="text-[10px] text-muted-foreground">
        Connecté en tant que {isAdmin ? 'admin' : 'super_admin'}. Les Edge Functions{' '}
        <code>admin-reset</code> et <code>admin-snapshot</code> tournent en service_role mais
        valident <code>users_metadata.role = &apos;super_admin&apos;</code> du caller avant
        d&apos;exécuter.
      </p>
    </div>
  );
}

function ScopeCard({
  scope,
  onClick,
  large,
}: {
  scope: ScopeMeta;
  onClick: () => void;
  large?: boolean;
}): JSX.Element {
  const Icon = scope.icon;
  const tone =
    scope.level === 'total'
      ? 'border-destructive bg-destructive/10'
      : scope.level === 'complete'
        ? 'border-warning bg-warning/10'
        : 'border-border bg-card';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!scope.available}
      className={cn(
        'group flex flex-col gap-2 rounded-lg border-2 p-4 text-left transition-shadow',
        tone,
        scope.available ? 'hover:shadow-md' : 'cursor-not-allowed opacity-50',
        large && 'md:flex-row md:items-center md:gap-4',
      )}
    >
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'rounded-md p-2',
            scope.level === 'total' && 'bg-destructive text-destructive-foreground',
            scope.level === 'complete' && 'bg-warning text-warning-foreground',
            scope.level === 'module' && 'bg-secondary',
          )}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold">{scope.title}</div>
          <div className="text-xs text-muted-foreground">
            {scope.available ? `Confirmation : ${scope.challenge}` : 'Module pas encore livré'}
          </div>
        </div>
      </div>
      {!large && (
        <ul className="space-y-0.5 pl-1 text-[11px] text-muted-foreground">
          {scope.consequences.slice(0, 3).map((c, i) => (
            <li key={i}>• {c}</li>
          ))}
        </ul>
      )}
      {large && (
        <Button
          variant={scope.level === 'total' ? 'destructive' : 'default'}
          size="sm"
          disabled={!scope.available}
        >
          {scope.level === 'total' ? <Skull className="h-4 w-4" /> : <Zap className="h-4 w-4" />}
          {scope.level === 'total' ? 'Lancer' : 'Lancer'}
        </Button>
      )}
    </button>
  );
}
